import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Tabela responsiva.
 *
 * Em tela larga é uma tabela; abaixo de `sm` cada linha vira um cartão, com o
 * rótulo da coluna ao lado do valor (ver `.tabela-empilha` em `index.css`).
 * A alternativa — rolar de lado para ler uma linha — é o tipo de coisa que faz
 * a recepcionista desistir e voltar ao computador.
 *
 * A marcação continua sendo `<table>`: a estrutura semântica é a mesma para
 * quem usa leitor de tela, muda só a apresentação.
 */
export function Tabela({
  className,
  empilhar = true,
  ...props
}: React.ComponentProps<'table'> & {
  /** Desligue quando a leitura em cartão for pior que a rolagem lateral. */
  empilhar?: boolean;
}) {
  return (
    <div className="border-borda w-full overflow-x-auto rounded-lg border">
      <table
        className={cn('w-full caption-bottom text-sm', empilhar && 'tabela-empilha', className)}
        {...props}
      />
    </div>
  );
}

export function TabelaCabecalho({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead className={cn('bg-silenciado [&_tr]:border-b', className)} {...props} />;
}

export function TabelaCorpo({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />;
}

export function TabelaLinha({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      className={cn('border-borda hover:bg-silenciado/60 border-b transition-colors', className)}
      {...props}
    />
  );
}

export function TabelaTitulo({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      scope="col"
      className={cn(
        'text-silenciado-texto h-11 px-4 text-left align-middle text-xs font-semibold tracking-wide uppercase',
        className,
      )}
      {...props}
    />
  );
}

export function TabelaCelula({
  className,
  rotulo,
  ...props
}: React.ComponentProps<'td'> & {
  /**
   * Nome da coluna, exibido ao lado do valor quando a linha vira cartão.
   * Sem ele, a célula ocupa a largura toda — é o que se quer nas ações.
   */
  rotulo?: string;
}) {
  return <td data-rotulo={rotulo} className={cn('px-4 py-3 align-middle', className)} {...props} />;
}

/** Estado vazio da tabela, ocupando a largura total. */
export function TabelaVazia({ colunas, children }: { colunas: number; children: React.ReactNode }) {
  return (
    <TabelaLinha>
      <td colSpan={colunas} className="text-silenciado-texto px-4 py-10 text-center text-sm">
        {children}
      </td>
    </TabelaLinha>
  );
}
